import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, RefreshCw, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useIntakeSources } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_API_MAP: Record<SpeciesFilter, string | undefined> = {
  all: undefined,
  dogs: "DOG",
  cats: "CAT",
  other: undefined,
};

const BAR_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(210 70% 50%)",
  "hsl(280 65% 55%)",
  "hsl(160 60% 45%)",
  "hsl(38 92% 50%)",
  "hsl(0 72% 51%)",
];

interface IntakeSourcesChartProps {
  days: number;
}

export function IntakeSourcesChart({ days }: IntakeSourcesChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");

  const apiSpecies = SPECIES_API_MAP[speciesFilter];
  const { data, isLoading, isError, refetch } = useIntakeSources(days, location, apiSpecies, 10);

  const chartData = useMemo(() => {
    if (!data) return [];
    if (speciesFilter === "other" && data.bySpecies?.OTHER) {
      return data.bySpecies.OTHER.sources.map((s) => ({
        name: truncateName(s.name, 25),
        fullName: s.name,
        count: s.count,
        percentage: s.percentage,
      }));
    }
    return (data.sources || []).map((s) => ({
      name: truncateName(s.name, 25),
      fullName: s.name,
      count: s.count,
      percentage: s.percentage,
    }));
  }, [data, speciesFilter]);

  const total = useMemo(() => {
    if (!data) return 0;
    if (speciesFilter === "other" && data.bySpecies?.OTHER) {
      return data.bySpecies.OTHER.total;
    }
    return data.total;
  }, [data, speciesFilter]);

  if (isLoading) {
    return (
      <Card data-testid="card-intake-sources">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Intake Sources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[350px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="card-intake-sources">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Intake Sources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-2">
              Failed to load intake source data.
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-intake-sources">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-intake-sources">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Intake Sources
          </CardTitle>
          {total > 0 && <span className="text-sm text-muted-foreground ml-1">({total} total)</span>}
        </div>
        <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
          <SelectTrigger className="w-[130px]" data-testid="select-intake-species">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="dogs">Dogs</SelectItem>
            <SelectItem value="cats">Cats</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 40)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12 }} className="text-muted-foreground" />
              <YAxis
                type="category"
                dataKey="name"
                width={180}
                tick={{ fontSize: 11 }}
                className="text-muted-foreground"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  borderColor: "hsl(var(--border))",
                  borderRadius: "6px",
                  color: "hsl(var(--card-foreground))",
                }}
                formatter={(value: number, _name: string, props: any) => [
                  `${value} animals (${props.payload.percentage}%)`,
                  props.payload.fullName,
                ]}
              />
              <Bar dataKey="count" name="Unique Animals" radius={[0, 4, 4, 0]}>
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground" data-testid="text-intake-sources-empty">
            No intake source data available for the selected filters.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function truncateName(name: string, maxLen: number): string {
  if (name.length <= maxLen) return name;
  return name.substring(0, maxLen - 1) + "\u2026";
}
